import { Row, Space } from "antd";
import React, { useEffect } from "react";

import { RawTable } from "@skydio/rivet-ui";

import ExportCSVButton from "components/common/files/ExportCSVButton";

import type { ColumnType } from "antd/lib/table";
import type { ReactElement, ReactNode } from "react";
import type { usePaginationFragmentHookType } from "react-relay/relay-hooks/usePaginationFragment";
import type { Connection } from "@skydio/relay-toolkit";
import type { PaginationProps } from "@skydio/rivet-ui";
import type { ExportColumnType } from "components/common/files/ExportCSVButton";

function getColumnKey<RecordT extends object>(
  column: ColumnType<RecordT>,
  defaultKey: React.Key
): React.Key {
  if ("key" in column && column.key !== undefined && column.key !== null) {
    return column.key;
  }

  if (column.dataIndex) {
    return Array.isArray(column.dataIndex)
      ? column.dataIndex.join(".")
      : (column.dataIndex as string | number);
  }

  return defaultKey;
}

function renderColumnTitle<RecordT extends object>(column: ColumnType<RecordT>, props: any) {
  if (typeof column.title === "function") {
    return column.title(props);
  }

  return column.title;
}

function getValue(
  entity: any,
  path: (string | number | symbol)[] | readonly (string | number | symbol)[]
) {
  let current = entity;

  for (let i = 0; i < path.length; i += 1) {
    if (current === null || current === undefined) {
      return undefined;
    }

    current = current[path[i]];
  }

  return current;
}

const isEmptyReactNode = (node: ReactNode): boolean => {
  // Check for null or undefined
  if (node == null) {
    return true;
  }

  // Check for empty strings or whitespace strings
  if (typeof node === "string" && node.trim() === "") {
    return true;
  }

  // Check for empty arrays (React fragments can be arrays)
  if (Array.isArray(node) && node.length === 0) {
    return true;
  }

  // Check for empty React elements
  if (React.isValidElement(node)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const element = node as ReactElement<any>;
    if (element.props.children == null || React.Children.count(element.props.children) === 0) {
      return true;
    }
  }

  return false;
};

const EmptyCell = () => (
  // emdash
  <div className="text-gray-300">
    <>{"—"}</>
  </div>
);

const renderCell = <RecordT extends { [key: string]: any }>(
  column: ColumnType<RecordT>,
  record: RecordT,
  index: number,
  useDefaultEmptyState: boolean
) => {
  let value = record;
  if (column.dataIndex) {
    value = Array.isArray(column.dataIndex)
      ? getValue(record, column.dataIndex)
      : record[column.dataIndex as string];
  }

  if (column.render) {
    const res = column.render(value, record, index) as React.ReactNode;
    if (useDefaultEmptyState && isEmptyReactNode(res)) {
      return EmptyCell();
    } else {
      return res;
    }
  }

  return value as unknown as React.ReactNode;
};

interface PaginatedTableProps<RecordT extends { [key: string]: any }> {
  paginationProps: PaginationProps;
  filters?: ReactNode;
  filterValues?: string[];
  dataSource: RecordT[];
  // columns can be generic column type or exportable column type
  columns: ColumnType<RecordT>[] | ExportColumnType<RecordT>[];
  height?: string;
  loading?: boolean;
  useDefaultEmptyState?: boolean;

  // if an exportFilename is provided, along with the pagination query & connection, this table
  // is exportable and an associated Export component will be rendered
  exportFilename?: string;
  paginationQuery?: usePaginationFragmentHookType<any, any, any>;
  connection?: Connection<RecordT> | null;
  expandedRows?: Array<boolean>;
  renderExpandedRow?: (record: RecordT, index: number) => React.ReactNode;
}

const PaginatedTable = <RecordT extends { [key: string]: any }>({
  paginationProps,
  dataSource,
  useDefaultEmptyState = false,
  columns,
  filters,
  filterValues,
  height,
  loading = false,
  exportFilename,
  paginationQuery,
  connection,
  expandedRows,
  renderExpandedRow,
}: PaginatedTableProps<RecordT>) => {
  useEffect(() => {
    paginationProps.resetPageNumber();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationProps.resetPageNumber, ...(filterValues || [])]);
  // construct list of visible columns in the table
  const visibleColumns = columns.filter(col => !(col as ExportColumnType<RecordT>).hideFromTable);
  // construct list of exportable columns (based on whether each col has the export function)
  const exportableColumns = columns.filter(col => !!(col as ExportColumnType<RecordT>).export);

  return (
    <Space direction="vertical" style={{ width: "100%", padding: "0px 5px" }}>
      <Row justify="space-between">
        <Space direction="horizontal">{filters || <div></div>}</Space>
        {exportFilename && paginationQuery && connection && (
          <ExportCSVButton
            paginationQuery={paginationQuery}
            connection={connection}
            columns={exportableColumns}
            fileName={exportFilename}
          />
        )}
      </Row>
      <RawTable>
        <RawTable.Header>
          <RawTable.Row>
            {visibleColumns.map((column, index) => (
              <RawTable.Head key={getColumnKey(column, index)}>
                {renderColumnTitle(column, {})}
              </RawTable.Head>
            ))}
          </RawTable.Row>
        </RawTable.Header>
        <RawTable.Body>
          {dataSource.map((record, i) => {
            const rowKey = record?.node?.uuid ?? record?.uuid ?? record?.node?.id ?? record?.id;
            return (
              <>
                <RawTable.Row key={rowKey}>
                  {visibleColumns.map((column, columnIndex) => (
                    <RawTable.Cell
                      key={getColumnKey(column, columnIndex)}
                      className={column.className}
                    >
                      {renderCell(column, record, i, useDefaultEmptyState)}
                    </RawTable.Cell>
                  ))}
                </RawTable.Row>
                {expandedRows?.[i] && (
                  <RawTable.Row key={rowKey + "-expanded"}>
                    <RawTable.Cell colSpan={Object.keys(visibleColumns).length}>
                      {renderExpandedRow?.(record, i)}
                    </RawTable.Cell>
                  </RawTable.Row>
                )}
              </>
            );
          })}
        </RawTable.Body>
        <RawTable.Pagination {...paginationProps} />
      </RawTable>
    </Space>
  );
};

export default PaginatedTable;
